import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { LoadingSpinner } from '@/components/Loading';
import { toast } from 'sonner';
import { API } from '@/App';

const STREAM_LABELS = {
  subscriptions: 'Subscriptions',
  workshops: 'Workshops',
  streams: 'Live Streams',
  marketplace: 'Marketplace',
  bookings: 'Bookings & Tips',
  ads: 'Ad / Spotlights',
};

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RevenueSnapshotCard = ({ title, subtitle, data, accent, variant }) => (
  <Card data-testid={`revenue-snapshot-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="text-sm text-gray-500 mb-1">{title}</div>
      <div className={`text-4xl font-bold mb-1 ${accent}`}>{money(data?.total)}</div>
      <div className="text-xs text-gray-400 mb-4">{subtitle}</div>
      <div className="space-y-2">
        {Object.entries(STREAM_LABELS).map(([key, label]) => {
          const s = data?.streams?.[key];
          if (!s || (!s.amount && !s.count)) return null;
          const zeroBooking = variant === 'net' && key === 'bookings';
          return (
            <div key={key} className="flex items-start justify-between text-sm">
              <span className="text-gray-500">
                {label}
                {zeroBooking && (
                  <span className="block text-[11px] text-gray-400 leading-tight">
                    Practitioner income · 0% commission
                  </span>
                )}
              </span>
              <span className="font-medium text-gray-700 text-right whitespace-nowrap">
                {money(s.amount)}
                <span className="text-gray-400 font-normal"> · {s.count}</span>
              </span>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

const MonthlyDigestCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    axios.get(`${API}/admin/monthly-digest/settings`, { headers: headers() })
      .then((r) => setEnabled(!!r.data.enabled))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const saveEnabled = async (val) => {
    setEnabled(val);
    try {
      await axios.post(`${API}/admin/monthly-digest/settings`, { enabled: val }, { headers: headers() });
      toast.success(`Monthly digest ${val ? 'enabled' : 'disabled'}`);
    } catch {
      setEnabled(!val);
      toast.error('Could not update setting');
    }
  };

  const sendNow = async () => {
    if (!window.confirm('Send the monthly digest email to ALL eligible users right now?')) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/monthly-digest/send-now`, {}, { headers: headers() });
      toast.success(`Digest queued — sending to ~${r.data.recipients_estimate} users in the background.`);
    } catch {
      toast.error('Failed to queue digest');
    } finally {
      setBusy(false);
    }
  };

  const preview = async () => {
    try {
      const r = await axios.get(`${API}/admin/monthly-digest/preview`, { headers: headers() });
      const w = window.open('', '_blank');
      if (w) { w.document.write(r.data.html); w.document.close(); }
    } catch {
      toast.error('Failed to load preview');
    }
  };

  return (
    <Card data-testid="monthly-digest-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-lg font-semibold text-gray-800">Monthly Email Digest</div>
            <div className="text-sm text-gray-500">
              Auto-sends on the 1st of each month to opted-in users.
            </div>
          </div>
          <Switch
            data-testid="monthly-digest-enable-switch"
            checked={enabled}
            disabled={!loaded}
            onCheckedChange={saveEnabled}
          />
        </div>
        <div className="flex gap-3 mt-4">
          <Button variant="outline" onClick={preview} data-testid="digest-preview-btn">Preview</Button>
          <Button onClick={sendNow} disabled={busy} data-testid="digest-send-now-btn">
            {busy ? 'Sending…' : 'Send Now'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!analytics) {
    return <div className="p-6">No analytics available</div>;
  }

  const StatCard = ({ title, value, subtitle, color = 'cyan' }) => (
    <Card>
      <CardContent className="p-6">
        <div className="text-sm text-gray-500 mb-1">{title}</div>
        <div className={`text-3xl font-bold text-${color}-600 mb-1`}>{value}</div>
        {subtitle && <div className="text-xs text-gray-400">{subtitle}</div>}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">
          Platform Analytics
        </h1>
        <p className="text-gray-300">Overview of your platform performance</p>
      </div>

      {/* Revenue Snapshot — Platform Net vs Gross Processing Volume */}
      {analytics.revenue_breakdown && (
        <div className="mb-8 space-y-8">
          {/* Platform Net Revenue */}
          <div data-testid="platform-net-revenue-section">
            <h2 className="text-xl font-semibold mb-1 text-white">Platform Net Revenue</h2>
            <p className="text-sm text-gray-300 mb-4">
              What the platform actually keeps — subscriptions, ads, and commission slices from
              workshops, live streams &amp; the marketplace.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RevenueSnapshotCard
                title="All-Time Net"
                subtitle="Platform earnings since launch"
                data={analytics.revenue_breakdown.platform_net.all_time}
                accent="text-green-600"
                variant="net"
              />
              <RevenueSnapshotCard
                title="Current Month Net"
                subtitle={`Month to date · ${analytics.revenue_breakdown.month_label}`}
                data={analytics.revenue_breakdown.platform_net.current_month}
                accent="text-cyan-600"
                variant="net"
              />
            </div>
          </div>

          {/* Gross Processing Volume */}
          <div data-testid="gmv-section">
            <h2 className="text-xl font-semibold mb-1 text-white">Gross Processing Volume (GMV)</h2>
            <p className="text-sm text-gray-300 mb-4">
              Total money processed through Stripe — includes practitioner payouts and full ticket
              &amp; order values. This is <span className="italic">not</span> platform income.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RevenueSnapshotCard
                title="All-Time GMV"
                subtitle="Everything processed since launch"
                data={analytics.revenue_breakdown.gmv.all_time}
                accent="text-purple-600"
                variant="gmv"
              />
              <RevenueSnapshotCard
                title="Current Month GMV"
                subtitle={`Month to date · ${analytics.revenue_breakdown.month_label}`}
                data={analytics.revenue_breakdown.gmv.current_month}
                accent="text-amber-600"
                variant="gmv"
              />
            </div>
          </div>

          <MonthlyDigestCard />
        </div>
      )}

      {/* User Statistics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-white">
          User Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          <StatCard 
            title="Total Users" 
            value={analytics.users.total}
            subtitle="All registered users"
          />
          <StatCard 
            title="Active Members" 
            value={analytics.users.active_members}
            subtitle="Paying members"
            color="green"
          />
          <StatCard 
            title="Free Comp" 
            value={analytics.users.free_comp}
            subtitle="Complimentary access"
            color="blue"
          />
          <StatCard 
            title="Archived" 
            value={analytics.users.archived}
            subtitle="Inactive users"
            color="gray"
          />
          <StatCard 
            title="Banned" 
            value={analytics.users.banned}
            subtitle="Restricted users"
            color="red"
          />
        </div>
        
        {/* Cancellation Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          <StatCard 
            title="Currently Cancelled" 
            value={analytics.users.cancelled}
            subtitle="Users with cancelled memberships"
            color="orange"
          />
          <StatCard 
            title="Total Cancellations" 
            value={analytics.cancellations.total}
            subtitle={`${analytics.cancellations.this_month} this month`}
            color="red"
          />
          <Card>
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-1">Total Refunds Issued</div>
              <div className="text-3xl font-bold text-red-600 mb-1">
                ${analytics.cancellations.total_refunds_issued.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-400">From cancellations</div>
            </CardContent>
          </Card>
        </div>
        
        {/* Cancellation Reasons Breakdown */}
        {analytics.cancellations.reasons && Object.keys(analytics.cancellations.reasons).length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2 text-white">Cancellation Reasons</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(analytics.cancellations.reasons).map(([reason, count]) => (
                <Card key={reason}>
                  <CardContent className="p-4">
                    <div className="text-xs text-gray-500 mb-1">{reason}</div>
                    <div className="text-2xl font-bold text-cyan-600">{count}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
      </div>

      {/* Content Statistics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-white">
          Content Statistics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard 
            title="Total Modules" 
            value={analytics.content.modules}
            subtitle="Learning modules"
          />
          <StatCard 
            title="Total Content Items" 
            value={analytics.content.total_contents}
            subtitle="Videos, blogs, exercises, etc."
          />
          <StatCard 
            title="Public Blogs" 
            value={analytics.content.public_blogs}
            subtitle="Accessible via URL"
            color="purple"
          />
        </div>
      </div>

      {/* Engagement Statistics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-white">
          Member Engagement
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard 
            title="Module Completions" 
            value={analytics.engagement.module_completions}
            subtitle="Total modules completed"
            color="green"
          />
          <StatCard 
            title="Content Completions" 
            value={analytics.engagement.content_completions}
            subtitle="Total content items completed"
            color="blue"
          />
          <StatCard 
            title="Completion Rate" 
            value={`${analytics.engagement.avg_completion_rate}%`}
            subtitle="Average across all users"
            color="cyan"
          />
        </div>
      </div>

      {/* Membership Tiers Statistics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-white">
          Membership Tiers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard 
            title="Bronze (Free)" 
            value={analytics.tiers.bronze}
            subtitle="Free tier members"
            color="orange"
          />
          <StatCard 
            title="Silver" 
            value={analytics.tiers.silver}
            subtitle="Silver tier members"
            color="gray"
          />
          <StatCard 
            title="Gold" 
            value={analytics.tiers.gold}
            subtitle="Gold tier members"
            color="yellow"
          />
          <StatCard 
            title="Platinum" 
            value={analytics.tiers.platinum}
            subtitle="Platinum tier members"
            color="purple"
          />
          <StatCard 
            title="Free Comp" 
            value={analytics.tiers.free_comp}
            subtitle="Complimentary access"
            color="blue"
          />
        </div>
      </div>

      {/* Add-ons Statistics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-white">
          Add-ons
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard 
            title="Drip Removal Add-ons" 
            value={analytics.tiers.drip_removal_addons}
            subtitle="Users who purchased drip removal"
            color="cyan"
          />
          <Card>
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-1">Add-on Revenue</div>
              <div className="text-3xl font-bold text-cyan-600 mb-1">
                {money(analytics.tiers.drip_removal_revenue)}
              </div>
              <div className="text-xs text-gray-400">Actually collected for drip removal</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-1">Add-on Conversion</div>
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {analytics.users.total > 0 ? ((analytics.tiers.drip_removal_addons / analytics.users.total) * 100).toFixed(1) : 0}%
              </div>
              <div className="text-xs text-gray-400">Of all users</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Revenue Statistics */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-white">
          Revenue
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard 
            title="Total Payments" 
            value={analytics.revenue.total_payments}
            subtitle="Successful transactions"
            color="green"
          />
          <Card className="bg-gradient-to-r from-green-50 to-cyan-50">
            <CardContent className="p-6">
              <div className="text-sm text-gray-500 mb-1">Total Revenue</div>
              <div className="text-4xl font-bold text-green-600 mb-1">
                ${analytics.revenue.total_revenue_dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-400">
                {analytics.revenue.total_payments} transactions
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Workshops & Streams */}
      {(analytics.workshops || analytics.streams) && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Workshops & Live Streams
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard 
              title="Total Workshops" 
              value={analytics.workshops?.total || 0}
              subtitle={`${analytics.workshops?.published || 0} published`}
              color="purple"
            />
            <StatCard 
              title="Workshop Purchases" 
              value={analytics.workshops?.total_purchases || 0}
              subtitle="Total sales"
              color="green"
            />
            <StatCard 
              title="Live Streams" 
              value={analytics.streams?.total || 0}
              subtitle="Created"
              color="cyan"
            />
            <StatCard 
              title="Stream Purchases" 
              value={analytics.streams?.total_purchases || 0}
              subtitle="Total sales"
              color="green"
            />
          </div>
        </div>
      )}

      {/* Marketplace */}
      {analytics.marketplace && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Marketplace
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Vendors" 
              value={analytics.marketplace.total_vendors}
              subtitle={`${analytics.marketplace.approved_vendors} approved`}
              color="cyan"
            />
            <StatCard 
              title="Products Listed" 
              value={analytics.marketplace.total_products}
              subtitle="Active listings"
            />
            <StatCard 
              title="Total Orders" 
              value={analytics.marketplace.total_orders}
              subtitle="Completed purchases"
              color="green"
            />
          </div>
        </div>
      )}

      {/* Bookings & Community */}
      {(analytics.bookings || analytics.community) && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Bookings & Community
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard 
              title="Total Bookings" 
              value={analytics.bookings?.total || 0}
              subtitle={`${analytics.bookings?.confirmed || 0} confirmed`}
              color="blue"
            />
            <StatCard 
              title="Forum Posts" 
              value={analytics.community?.forum_posts || 0}
              subtitle="Community discussions"
            />
            <StatCard 
              title="Forum Reports" 
              value={analytics.community?.forum_reports || 0}
              subtitle="Flagged content"
              color="red"
            />
            <StatCard 
              title="Active Contributors" 
              value={analytics.community?.active_contributors || 0}
              subtitle="Contributing members"
              color="purple"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
